/** tm/legal/useLegalFeed.ts — 주제 피드 목록 (필터 변경 시 1페이지부터, 「더 보기」로 이어 받기, 검색어 300ms 디바운스) */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getFeed } from './legalApi';
import { errMsg } from './legalTheme';
import type { FeedFacets, FeedQuery, FeedRow } from './legalTypes';

const PAGE_SIZE = 30;

/** 목록 카드 즉시 반영 — 읽음·별표·NEW · 내 제목(법령·판례·해석) */
export type RowPatch = Partial<Pick<FeedRow, 'unread' | 'starred' | 'is_new'>> & { custom_title?: string | null };
export interface FeedState {
  rows: FeedRow[]; total: number; loading: boolean; error: string; hasMore: boolean;
  /** 지금 조건의 구분별 건수 (서버가 주면) */
  facets: FeedFacets | null;
  loadMore: () => void; reload: () => void;
  patch: (kind: FeedRow['kind'], id: number, p: RowPatch) => void;
}

export function useLegalFeed(query: FeedQuery, enabled = true): FeedState {
  const [q, setQ] = useState(query.q);
  useEffect(() => { const t = setTimeout(() => setQ(query.q), 300); return () => clearTimeout(t); }, [query.q]);
  const eff: FeedQuery = { ...query, q };
  const key = JSON.stringify(eff);

  const [rows, setRows] = useState<FeedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<FeedFacets | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);

  const load = useCallback(async (k: string, p: number) => {
    const my = ++seq.current;
    setLoading(true); setError('');
    try {
      const d = await getFeed(JSON.parse(k) as FeedQuery, p, PAGE_SIZE);
      if (my !== seq.current) return;
      const got = d?.rows || [];
      setRows(prev => (p === 1 ? got : [...prev, ...got.filter(r => !prev.some(x => x.kind === r.kind && x.id === r.id))]));
      setTotal(Number(d?.total) || 0);
      if (p === 1) setFacets(d?.facets && typeof d.facets === 'object' ? d.facets : null);
      setPage(p);
    } catch (e) {
      if (my !== seq.current) return;
      setError(errMsg(e, '목록을 불러오지 못했습니다'));
      if (p === 1) { setRows([]); setTotal(0); setFacets(null); } // 이전 주제·탭 카드가 남아 헷갈리지 않게 비움 (더 보기 실패는 유지)
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) load(key, 1); }, [key, nonce, load, enabled]);

  const loadMore = useCallback(() => { if (!loading) load(key, page + 1); }, [key, page, loading, load]);
  const reload = useCallback(() => setNonce(n => n + 1), []);
  const patch = useCallback<FeedState['patch']>((kind, id, p) => {
    setRows(prev => prev.map(r => (r.kind === kind && r.id === id ? { ...r, ...p } as FeedRow : r)));
  }, []);

  return { rows, total, loading, error, facets, hasMore: rows.length < total, loadMore, reload, patch };
}
